import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import {
  amazonAsinKey,
  cartAwaitsNewRecords,
  cartDealsEnabled,
  cartRowDeal,
  liveCartDeals,
} from 'src/components/deals/amazonCartDeals';
import { cartDealsQuery } from 'src/components/deals/amazonItemDealsQuery';
import { AMAZON_ITEM_RECHECK_MS } from 'src/components/deals/amazonItemFilters';
import type { CartAmazonDeal, CartGetData, CartGetItem } from 'src/types';
import { useGetCartAPI, useNow } from '.';

type Props = {
  cartId: string;
  cartQuery: { isSuccess: boolean; data?: CartGetData };
  page: Parameters<typeof cartDealsEnabled>[0];
};

export const useCartRowDeals = ({ cartId, cartQuery, page }: Props) => {
  const queryClient = useQueryClient();
  const getCartAPI = useGetCartAPI();
  const now = useNow(AMAZON_ITEM_RECHECK_MS);
  const cart = cartQuery.isSuccess ? cartQuery.data : undefined;
  const showCartDeals = cartDealsEnabled(page, cart);
  const cartDealsResult = useQuery({ ...cartDealsQuery(cartId), enabled: showCartDeals });
  const deals = showCartDeals ? liveCartDeals(cartDealsResult) : undefined;
  const reloadedCartId = useRef<string>();

  // The deals request writes the records a cart loaded without; one reload shows them as a refresh would.
  // It is fetched outside the cart query, so a failed reload never puts that query in error, which leaves the page.
  useEffect(() => {
    if (
      !cart ||
      reloadedCartId.current === cartId ||
      !cartAwaitsNewRecords(cart.items, deals, now)
    ) {
      return;
    }
    reloadedCartId.current = cartId;
    getCartAPI(cartId, { reload: true })
      .then((reloaded) => queryClient.setQueryData(['cart', cartId], reloaded))
      .catch(() => undefined);
  }, [cart, cartId, deals, now, getCartAPI, queryClient]);

  if (!showCartDeals) return undefined;
  return (item: CartGetItem): CartAmazonDeal | null =>
    cartRowDeal(deals?.[amazonAsinKey(item.asin)], item.price, item.priceAsOf, now);
};
